import { useEffect, useState } from "react";
import { apiFetch, TENANT } from "../api.js";
import { groupRunsByScenario } from "../../domain/runGroups.js";
import { dayCT, formatCT } from "../time.js";

interface RunSummary {
  runId: string;
  createdAt: string;
  scenarioId: string | null;
  scenarioName: string | null;
  source: string | null;
  runBy: string | null;
  solverStatus: string;
  wallTimeS: number | null;
  objectiveValue: number | null;
  candidateCount: number | null;
  churn: number | null;
  intentText: string | null;
  compiledBy: string | null;
  infeasibleCore: string[];
  hasPayload: boolean;
  scenarioStatus?: string | null;
}

interface RunDetail extends RunSummary {
  spec: any | null;
  request: any | null;
  response: any | null;
  moves: { phaseId: string; step?: string; from: string | null; to: string }[] | null;
  explanation: string | null;
  unmapped: string[];
  absences: string[];
  rejectedTerms: string[];
  model: string | null;
}

/**
 * What the optimizer was given and what it returned.
 *
 * Reads top-to-bottom as the run happened: what was asked, what that compiled
 * to, what the solver was handed, what it decided, what actually moved. The
 * raw payloads sit at the bottom because they answer a different question —
 * a scheduler wants to understand the decision, an engineer wants to
 * reproduce it, and burying the JSON serves the first without losing the
 * second.
 */
export function RunLog() {
  const [runs, setRuns] = useState<RunSummary[] | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [raw, setRaw] = useState<"request" | "response" | null>(null);

  useEffect(() => { void loadList(); }, []);
  useEffect(() => { if (sel) void loadOne(sel); }, [sel]);

  async function loadList() {
    setErr(null);
    const r = await apiFetch(`/v1/sentry/runs?tenantId=${TENANT}&limit=100`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(j.error ?? `HTTP ${r.status}`); setRuns([]); return; }
    setRuns(j.runs ?? []);
    if (!sel && j.runs?.length) setSel(j.runs[0].runId);
  }

  async function loadOne(id: string) {
    setDetail(null); setRaw(null);
    const r = await apiFetch(`/v1/sentry/runs/${id}?tenantId=${TENANT}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(j.error ?? `HTTP ${r.status}`); return; }
    setDetail(j);
  }

  if (err) return <div className="empty">{err}</div>;
  if (!runs) return <div className="empty">Loading the optimizer log…</div>;
  if (!runs.length) {
    return (
      <div className="empty">
        No optimizer runs yet. Create a scenario and describe what you want changed — every run
        is recorded here.
      </div>
    );
  }

  return (
    <div className="runs">
      <div className="runs-list">
        {groupRunsByScenario(runs).map((g) => (
          <section className="run-grp" key={g.scenarioId ?? "none"}>
            <header className="run-grp-head">
              <div className="run-grp-name">
                {g.scenarioName ?? "Before scenarios were tracked"}
                {g.status === "published" && <span className="chip live">live</span>}
                {g.scenarioId && !g.status && <span className="chip">discarded</span>}
              </div>
              <div className="run-grp-meta mono muted">
                {g.solves} {g.solves === 1 ? "solve" : "solves"}
                {g.failures ? ` · ${g.failures} produced nothing` : ""}
                {g.totalMoved ? ` · ${g.totalMoved} moved` : ""}
              </div>
            </header>

            {g.runs.map((r) => (
              <button
                key={r.runId}
                className={`run-item${r.runId === sel ? " on" : ""}`}
                onClick={() => setSel(r.runId)}
              >
                <div className="run-item-top">
                  <span className="mono muted it">#{r.iteration}</span>
                  <span className={`badge ${statusClass(r.solverStatus)}`}>{r.solverStatus}</span>
                  <span className="spacer" />
                  <span className="mono muted">{when(r.createdAt)}</span>
                </div>
                <div className="run-item-intent">{r.intentText ?? "(no stated intent)"}</div>
                <div className="run-item-meta mono muted">
                  {r.source ?? "—"} · {fmtS(r.wallTimeS)} · {r.churn ?? 0} moved
                </div>
              </button>
            ))}
          </section>
        ))}
      </div>

      <div className="run-detail">
        {!detail ? (
          <div className="empty">Loading…</div>
        ) : (
          <>
            <Section n={1} title="What was asked">
              <p className="say">{detail.intentText ?? "No intent — the spec was supplied directly."}</p>
              <Facts
                rows={[
                  ["written by", detail.compiledBy ?? "—"],
                  ["model", detail.model ?? "—"],
                  ["run by", detail.runBy ?? "—"],
                  ["scenario", detail.scenarioName ?? detail.scenarioId?.slice(0, 8) ?? "—"],
                  // Composed runs carry how many requirements were enforced,
                  // which is the difference between "one ask" and "the sixth
                  // thing asked of this scenario, with five still holding".
                  [
                    "requirements enforced",
                    (detail.spec as any)?.meta?.requirements === undefined
                      ? "—"
                      : String((detail.spec as any).meta.requirements),
                  ],
                  ["when", formatCT(detail.createdAt)],
                ]}
              />
            </Section>

            <Section n={2} title="What that compiled to">
              {!detail.spec ? (
                <div className="muted">No spec recorded.</div>
              ) : (
                <>
                  {(detail.spec.hard ?? []).map((t: any) => (
                    <div className="term" key={t.id}>
                      <b>hard</b> {t.term} <span className="sel">{JSON.stringify(t.select)}</span>
                      {t.bound ? ` → ${t.bound}` : ""}
                      {typeof t.pct === "number" ? ` → ${t.pct}%` : ""}
                    </div>
                  ))}
                  {(detail.spec.soft ?? []).map((t: any) => (
                    <div className="term" key={t.id}>
                      <b className="soft">soft</b> {t.term}{" "}
                      <span className="sel">{JSON.stringify(t.select)}</span> · weight {t.weight}
                    </div>
                  ))}
                  {!((detail.spec.hard ?? []).length + (detail.spec.soft ?? []).length) &&
                    !(detail.absences ?? []).length && (
                      <div className="muted">No terms — the solver was left to its default objective.</div>
                    )}
                </>
              )}

              {/* Applied to the calendar rather than compiled into the spec, so
                  without this the one thing the request was about would be the
                  one thing this card could not show. */}
              {!!(detail.absences ?? []).length && (
                <div className="run-absence">
                  <b>Recorded as time off:</b>
                  {detail.absences.map((a) => (
                    <div className="day" key={a}>{a}</div>
                  ))}
                  <div className="muted">
                    Written to the calendar for those days only. A constraint term carries no dates,
                    so it would have held for the whole horizon.
                  </div>
                </div>
              )}

              {/* Both of these are silent quality losses, so they are stated
                  rather than left for someone to notice in the result. */}
              {!!detail.unmapped.length && (
                <div className="run-warn">
                  <b>Could not be expressed:</b> {detail.unmapped.map((u) => `“${u}”`).join(", ")}
                  <div className="muted">Left out rather than approximated — no term covers it.</div>
                </div>
              )}
              {!!detail.rejectedTerms.length && (
                <div className="run-warn">
                  <b>Vocabulary the solver does not implement:</b> {detail.rejectedTerms.join(" · ")}
                  <div className="muted">Offered to nobody, so it could not be silently ignored.</div>
                </div>
              )}
            </Section>

            <Section n={3} title="What the solver was given">
              {detail.request ? (
                <Facts
                  rows={[
                    ["phases to place", String(detail.request.tasks?.length ?? 0)],
                    ["people", String(detail.request.resources?.length ?? 0)],
                    ["horizon", `${detail.request.horizonBlocks ?? 0} half-days`],
                    ["orderings", String(detail.request.precedences?.length ?? 0)],
                    ["assumptions", String(detail.request.assumptions?.length ?? 0)],
                    ["objective terms", String(detail.request.objective?.length ?? 0)],
                    ["time limit", fmtS(detail.request.timeLimitS)],
                  ]}
                />
              ) : (
                <div className="muted">{NO_PAYLOAD}</div>
              )}
            </Section>

            <Section n={4} title="What it decided">
              {detail.solverStatus === "ERROR" && (
                <div className="run-warn">
                  <b>This attempt never reached the solver.</b>
                  <div className="muted">
                    The request was understood and compiled — section 2 shows what it became — but
                    the run failed before a schedule could be produced.
                  </div>
                </div>
              )}
              <Facts
                rows={[
                  ["status", detail.solverStatus],
                  ["time taken", fmtS(detail.wallTimeS)],
                  ["objective value", detail.objectiveValue === null ? "—" : String(detail.objectiveValue)],
                  ["solution found", detail.candidateCount ? "yes" : "no"],
                ]}
              />
              {!!detail.infeasibleCore.length && (
                <div className="run-warn">
                  <b>These could not all hold at once:</b> {detail.infeasibleCore.join(" · ")}
                  <div className="muted">
                    The smallest set the solver could find — removing any one of them may be enough.
                  </div>
                </div>
              )}
              {detail.explanation && <p className="say">{detail.explanation}</p>}
              {/* Empty in the normal case. Non-empty means the solver had to
                  floor or clamp a weight to fit its fixed-point arithmetic —
                  the objective that ran is not quite the one that was asked
                  for, and a strange schedule with no note here would be
                  unexplainable. */}
              {!!(detail.response?.objectiveNotes ?? []).length && (
                <div className="run-warn">
                  <b>Objective terms the solver had to adjust:</b>
                  {(detail.response.objectiveNotes as
                    { kind: string; weight: number; action: string; detail: string }[]
                  ).map((n, i) => (
                    <div className="term" key={i}>
                      {n.kind} · weight {n.weight} · {n.action} — {n.detail}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section n={5} title="What actually moved">
              <Facts rows={[["work changing hands", `${detail.churn ?? 0} phases`]]} />
              {detail.moves?.length ? (
                <div className="run-moves">
                  {detail.moves.map((m) => (
                    <div className="diff-row" key={m.phaseId}>
                      <span className="mono">{m.step ?? m.phaseId}</span>
                      <span className="mono muted">{m.from ?? "pool"} → {m.to}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="muted">Nobody's work changed owner.</div>
              )}
            </Section>

            <Section n={6} title="Raw payload">
              {!detail.hasPayload ? (
                <div className="muted">{NO_PAYLOAD} The narrative above is kept for every run.</div>
              ) : (
                <>
                  <div className="run-raw-tabs">
                    <button className={`btn${raw === "request" ? " on" : ""}`}
                      onClick={() => setRaw(raw === "request" ? null : "request")}>
                      Request
                    </button>
                    <button className={`btn${raw === "response" ? " on" : ""}`}
                      onClick={() => setRaw(raw === "response" ? null : "response")}>
                      Response
                    </button>
                    {raw && (
                      <button
                        className="btn"
                        onClick={() => void navigator.clipboard?.writeText(
                          JSON.stringify(raw === "request" ? detail.request : detail.response, null, 2),
                        )}
                      >
                        Copy JSON
                      </button>
                    )}
                  </div>
                  {raw && (
                    <pre className="run-raw">
                      {JSON.stringify(raw === "request" ? detail.request : detail.response, null, 2)}
                    </pre>
                  )}
                </>
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Two different reasons a run has no raw payload — it predates recording, or
 * it aged past the retention window. The row cannot tell them apart, so the
 * copy must not claim one. Saying "pruned" about a run that never had a
 * payload is simply untrue.
 */
const NO_PAYLOAD =
  "No raw payload for this run — either it predates payload recording, or it has aged out of the most recent 200.";

function Section({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="run-sec">
      <h3><span className="run-sec-n">{n}</span>{title}</h3>
      {children}
    </section>
  );
}

function Facts({ rows }: { rows: [string, string][] }) {
  return (
    <dl className="facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd className="mono">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function statusClass(s: string): string {
  if (s === "OPTIMAL" || s === "FEASIBLE") return "good";
  // ERROR is a failed attempt — it never reached the solver. Same color as
  // INFEASIBLE because both mean "no schedule came out of this".
  if (s === "INFEASIBLE" || s === "ERROR") return "crit";
  return "";
}

const fmtS = (s: number | null | undefined) => (s === null || s === undefined ? "—" : `${Number(s).toFixed(1)}s`);

function when(iso: string): string {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return dayCT(iso);
}
